import React, { useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Settings } from '../../icon/Settings';
import { Close } from '../../icon/Close';

import './slideout.css';

function Slideout({
  className,
  children,
  isOpen,
  headingLabel,
  headingIcon,
  closeLabel,
  hideButtons,
  backdropBehavior,
  onClose,
  actions,
}) {
  const slideoutRef = useRef(null);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape' && isOpen) {
      onClose();
    }
  }, [isOpen, onClose]);

  const handleClickOutside = useCallback((event) => {
    if (backdropBehavior === 'close'
      && isOpen
      && slideoutRef.current
      && !slideoutRef.current.contains(event.target)
    ) {
      onClose();
    }
  }, [isOpen, backdropBehavior, onClose]);

  useEffect(() => {
    const { body } = document;
    const menuSection = body ? body.querySelector('.main-section-menu') : null;
    if (isOpen) {
      if (body) {
        body.classList.add('filters-active');
        if (backdropBehavior === 'click-through') {
          body.classList.add('filters-active--allow-click-through');
        }
      }
      if (menuSection) {
        menuSection.style.zIndex = 'unset';
      }
    } else {
      if (body) {
        body.classList.remove('filters-active');
        if (backdropBehavior === 'click-through') {
          body.classList.remove('filters-active--allow-click-through');
        }
      }
      if (menuSection) {
        menuSection.style.zIndex = null;
      }
    }
  }, [isOpen, backdropBehavior]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    if (backdropBehavior === 'close') {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (backdropBehavior === 'close') {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      }
    };
  }, [handleKeyDown, handleClickOutside, backdropBehavior]);

  return (
    <div
      ref={slideoutRef}
      className={[
        'filters-content-drop',
        'react-slideout',
        isOpen ? 'react-slideout--open' : 'react-slideout--close',
        className,
      ].filter(Boolean).join(' ')}
    >
      <button
        className="drop-heading"
        type="button"
        onClick={onClose}
      >
        <span className="title">
          {headingIcon || <Settings />}
          {headingLabel}
        </span>
        <span className="icon-close">
          <span>Close</span>
          <Close />
        </span>
      </button>
      <div className="filters-drop-inner-holder">
        <div className="filters-drop-inner">
          {children}
        </div>
      </div>
      {!hideButtons && (
        <div className="btn-results-holder">
          <button
            type="button"
            onClick={onClose}
          >
            {closeLabel}
          </button>
          {actions.map((action) => (
            <button
              key={action.key}
              className="react-slideout-action"
              type="button"
              onClick={action.onClick}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export { Slideout };

Slideout.propTypes = {
  className: PropTypes.string,
  children: PropTypes.element,
  isOpen: PropTypes.bool,
  headingLabel: PropTypes.string,
  headingIcon: PropTypes.element,
  closeLabel: PropTypes.string,
  hideButtons: PropTypes.bool,
  backdropBehavior: PropTypes.oneOf([null, 'none', 'click-through', 'close']),
  onClose: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    onClick: PropTypes.func,
  })),
};

Slideout.defaultProps = {
  className: null,
  children: null,
  isOpen: false,
  headingLabel: 'Filters',
  headingIcon: null,
  closeLabel: 'Close',
  hideButtons: false,
  backdropBehavior: null,
  onClose: () => {},
  actions: [],
};
